import React, {Fragment, useState} from "react";
import pxToRem from "hooks/usePxToRem";

import {Stack, Typography} from "@mui/material";
import {Grid} from "@mui/system";

import {CustomToggle, CustomAutocomplete, CustomAutoComplete} from "ui";
import {AutoCompleteData} from "ui/CustomAutoComplete/CustomAutoComplete";

import {DefaultCardStructure} from "components";
import {TypeLoad, WoodPackage} from "util/globalEnums";
import {useFormContext, Controller, useFieldArray} from "react-hook-form";
import useChangeDAteAndAutocomplete from "views/ProcessDetails/hooks/useChangeDAteAndAutocomplete";
import useHandleFieldLists from "views/ProcessDetails/hooks/useHandleFieldLists";
import {
  GetCommoditiesList,
  GetCurrenciesList,
} from "services/api_v2/Processes/Processes.service";
import {
  GetPersonsByTypes,
  PersonTypes,
} from "services/api_v2/Persons/Persons.service";
import {getTooltipText} from "views/ProcessDetails/utils/getTooltipText";
import ProcessSelect from "views/ProcessDetails/refactor/components/ProcessSelect";
import ProcessTextField from "views/ProcessDetails/refactor/components/ProcessTextField";
import {Process} from "views/ProcessDetails/refactor/types/process.types";

const GeneralLoadInfo = () => {
  const {control, watch} = useFormContext<Process>();

  const {fields} = useFieldArray({
    name: "processLoads",
  });

  const [insurerOptions, setInsurerOptions] = useState<AutoCompleteData[]>(
    [] as AutoCompleteData[],
  );

  const {handleChangeAutocomplete, mountOptions} =
    useChangeDAteAndAutocomplete();

  const [currenciesOptions, setCurrenciesOptions] = useState<
    AutoCompleteData[]
  >([] as AutoCompleteData[]);

  const [commoditiesOptions, setCommoditiesOptions] = useState<
    AutoCompleteData[]
  >([] as AutoCompleteData[]);

  const [searchFields, setSearchFields] = useState({
    currencies: "",
    commodities: "",
    insurer: "",
  });

  const {
    getOptions: getCurrenciesOptions,
    onBottomScroll: onBottomScrollCurrencies,
    isLoadingOptions: isLoadingCurrenciesOptions,
    pageState: currenciesPageState,
  } = useHandleFieldLists(GetCurrenciesList);

  const {
    getOptions: getCommoditiesOptions,
    onBottomScroll: onBottomScrollCommodities,
    isLoadingOptions: isLoadingCommoditiesOptions,
  } = useHandleFieldLists(GetCommoditiesList);

  const {
    getOptions: getInsurerOptions,
    onBottomScroll: onBottomScrollInsurer,
    isLoadingOptions: isLoadingInsurerOptions,
    pageState: insurerPageState,
  } = useHandleFieldLists(GetPersonsByTypes, null, [PersonTypes.insurer]);

  const isAiProcess =
    `${watch("operation")?.[0]}${watch("modality")?.[0]}` === "IA";

  return (
    <DefaultCardStructure
      title="Carga"
      linkId="load"
      hasDivider={true}
      isSubcard={false}
    >
      {fields?.length === 0 && (
        <Typography>Não existem cargas no processo.</Typography>
      )}
      {fields?.map((field, index) => (
        <React.Fragment key={field.id}>
          <Grid
            container
            mb={pxToRem(20)}
            rowSpacing={pxToRem(16)}
            columnSpacing={pxToRem(8)}
            key={field.id}
          >
            <Grid size={2.4}>
              <Controller
                name={
                  `processLoads.${index}.typeLoad` as `processLoads.0.typeLoad`
                }
                control={control}
                render={({field: {onChange, value}}) => (
                  <ProcessSelect
                    name={`processLoads.${index}.typeLoad`}
                    value={value}
                    onChange={onChange}
                    data={Object.values(TypeLoad)}
                    size="small"
                    fullWidth
                    label="Tipo"
                    disabled={isAiProcess}
                  />
                )}
              />

              <Stack
                direction="row"
                justifyContent="flex-start"
                spacing={pxToRem(20)}
                sx={{
                  marginTop: pxToRem(-4),
                  maxHeight: pxToRem(32),
                  ml: pxToRem(5),
                }}
              >
                <Controller
                  name={
                    `processLoads.${index}.cargoDangerous` as `processLoads.0.cargoDangerous`
                  }
                  control={control}
                  render={({field: {onChange, value}}) => (
                    <CustomToggle
                      name={`processLoads[${index}].cargoDangerous`}
                      label="Perigosa"
                      labelPosition="end"
                      onToggleSwitch={onChange}
                      isChecked={value}
                      isDisabled
                    />
                  )}
                />
                <Controller
                  name={
                    `processLoads.${index}.isInsurance` as `processLoads.0.isInsurance`
                  }
                  control={control}
                  render={({field: {onChange, value}}) => (
                    <CustomToggle
                      name={`processLoads[${index}].isInsurance`}
                      label="Seguro"
                      labelPosition="end"
                      onToggleSwitch={onChange}
                      isChecked={value}
                      isDisabled
                    />
                  )}
                />
              </Stack>
            </Grid>

            <Grid size={2.4}>
              <Controller
                name={
                  `processLoads.${index}.commodity` as `processLoads.0.commodity`
                }
                control={control}
                render={({field: {value}}) => (
                  <CustomAutocomplete
                    fullWidth
                    textFieldProps={{
                      label: "Mercadoria",
                    }}
                    name={`processLoads.${index}.commodity`}
                    placeholder="Mercadoria"
                    disabled
                    tooltipText={getTooltipText(value?.name)}
                    loading={isLoadingCommoditiesOptions}
                    options={mountOptions(value, commoditiesOptions)}
                    value={value?.name ?? ""}
                    onOpen={() =>
                      getCommoditiesOptions(setCommoditiesOptions, 1, "")
                    }
                    onInputChange={(e, value) => {
                      if (e !== null) {
                        setSearchFields((prev) => ({
                          ...prev,
                          commodities: value,
                        }));
                        getCommoditiesOptions(setCommoditiesOptions, 1, value);
                      }
                    }}
                    getNextPage={() => {
                      onBottomScrollCommodities(
                        setCommoditiesOptions,
                        searchFields.commodities,
                      );
                    }}
                    onChange={(e, value) => {
                      handleChangeAutocomplete(
                        e,
                        value,
                        `processLoads[${index}].commodity`,
                      );
                    }}
                    isOptionEqualToValue={(option, value) =>
                      value === option?.label
                    }
                  />
                )}
              />
            </Grid>

            <Grid size={2.4}>
              <Controller
                name={
                  `processLoads.${index}.currencyCommodity` as `processLoads.0.currencyCommodity`
                }
                control={control}
                render={({field: {value}}) => (
                  <CustomAutoComplete
                    fullWidth
                    label="Moeda Da Invoice"
                    name={`processLoads.${index}.currencyCommodity`}
                    size="small"
                    placeholder="Moeda Da Invoice"
                    isDisabled
                    isLoading={isLoadingCurrenciesOptions}
                    value={value?.name ?? ""}
                    tooltipText={getTooltipText(value?.name)}
                    onChangeFn={(e, value) =>
                      handleChangeAutocomplete(
                        e,
                        value,
                        `processLoads[${index}].currencyCommodity`,
                      )
                    }
                    options={currenciesOptions}
                    onOpen={() => getCurrenciesOptions(setCurrenciesOptions)}
                    onInputChange={(e, value) => {
                      if (e !== null) {
                        getCurrenciesOptions(setCurrenciesOptions, 1, value);
                      }
                    }}
                    onBottomScroll={() =>
                      onBottomScrollCurrencies(setCurrenciesOptions)
                    }
                    hasMorePagesToLoad={
                      currenciesPageState.currentPage <
                      currenciesPageState.lastPage
                    }
                  />
                )}
              />
            </Grid>

            <Grid size={2.4}>
              <Controller
                name={
                  `processLoads.${index}.commodityValue` as `processLoads.0.commodityValue`
                }
                control={control}
                render={({field: {onChange, value}}) => (
                  <ProcessTextField
                    name={`processLoads[${index}].commodityValue`}
                    value={value}
                    disabled
                    placeholder="Valor"
                    onChange={onChange}
                    size="small"
                    fullWidth
                    label="Valor Da Mercadoria"
                  />
                )}
              />
            </Grid>

            <Grid size={2.4}>
              <Controller
                name={
                  `processLoads.${index}.woodPackage` as `processLoads.0.woodPackage`
                }
                control={control}
                render={({field: {onChange, value}}) => (
                  <ProcessSelect
                    name={`processLoads[${index}].woodPackage`}
                    value={value ?? ""}
                    onChange={onChange}
                    data={Object.values(WoodPackage)}
                    size="small"
                    fullWidth
                    label="Wood Package"
                  />
                )}
              />
            </Grid>

            <Grid size={2.4}>
              <Controller
                name={`quantityUn` as `quantityUn`}
                control={control}
                render={({field: {onChange, value}}) => (
                  <ProcessTextField
                    name={`quantityUn`}
                    placeholder="Quantidade UN"
                    value={value}
                    onChange={onChange}
                    size="small"
                    disabled
                    fullWidth
                    label="Quantidade UN"
                    inputProps={{
                      style: {
                        fontSize: 14,
                      },
                      type: "number",
                    }}
                  />
                )}
              />
            </Grid>
            {isAiProcess && (
              <>
                <Grid size={2.4}>
                  <Controller
                    name={
                      `processLoads.${index}.refrigeratedCargo` as `processLoads.0.refrigeratedCargo`
                    }
                    control={control}
                    render={({field: {onChange, value}}) => (
                      <ProcessSelect
                        data={[
                          {name: "SIM", value: true},
                          {name: "NÃO", value: false},
                        ]}
                        label="Carga Refrigerada"
                        value={value}
                        name={
                          `processLoads.${index}.refrigeratedCargo` as `processLoads.0.refrigeratedCargo`
                        }
                        onChange={onChange}
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid size={2.4}>
                  <Controller
                    name={
                      `processLoads.${index}.temperature` as `processLoads.0.temperature`
                    }
                    control={control}
                    render={({field: {onChange, value}}) => (
                      <ProcessTextField
                        name={`processLoads[${index}].temperature`}
                        placeholder="Temperatura"
                        value={value}
                        onChange={onChange}
                        size="small"
                        fullWidth
                        label="Temperatura"
                        inputProps={{
                          style: {
                            fontSize: 14,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid size={2.4}>
                  <Controller
                    name={
                      `processLoads.${index}.isOnlyFreightCargo` as `processLoads.0.isOnlyFreightCargo`
                    }
                    control={control}
                    render={({field: {onChange, value}}) => (
                      <ProcessSelect
                        data={[
                          {name: "CARGA E FRETE", value: true},
                          {name: "TODAS AS GARANTIAS", value: false},
                        ]}
                        label="Apólice de seguro"
                        value={value}
                        name={
                          `processLoads.${index}.isOnlyFreightCargo` as `processLoads.0.isOnlyFreightCargo`
                        }
                        onChange={onChange}
                        size="small"
                      />
                    )}
                  />
                </Grid>
              </>
            )}
            {watch(`processLoads.${index}.isInsurance`) && (
              <Fragment>
                <Grid size={2.4}>
                  <Controller
                    name={
                      `processLoads.${index}.insuredAmount` as `processLoads.0.insuredAmount`
                    }
                    control={control}
                    render={({field: {onChange, value}}) => (
                      <ProcessTextField
                        name={`processLoads[${index}].insuredAmount`}
                        placeholder="Importância segurada"
                        value={value}
                        onChange={onChange}
                        size="small"
                        disabled
                        fullWidth
                        label="Importância Segurada"
                      />
                    )}
                  />
                </Grid>

                <Grid size={2.4}>
                  <Controller
                    name={
                      `processLoads.${index}.insurancePercentage` as `processLoads.0.insurancePercentage`
                    }
                    control={control}
                    render={({field: {onChange, value}}) => (
                      <ProcessTextField
                        name={`processLoads.${index}.insurancePercentage`}
                        type="number"
                        placeholder="% Seguro"
                        value={value}
                        onChange={onChange}
                        size="small"
                        disabled
                        fullWidth
                        label="Percentual Seguro"
                      />
                    )}
                  />
                </Grid>
                <Grid size={2.4}>
                  <Controller
                    name={
                      `processLoads.${index}.insurer` as `processLoads.0.insurer`
                    }
                    control={control}
                    render={({field: {value}}) => (
                      <CustomAutoComplete
                        fullWidth
                        label="Seguradora"
                        size="small"
                        name={`processLoads[${index}].insurer`}
                        placeholder="Seguradora"
                        isLoading={isLoadingInsurerOptions}
                        value={value?.name ?? ""}
                        tooltipText={getTooltipText(value?.name)}
                        onChangeFn={(e, value) =>
                          handleChangeAutocomplete(
                            e,
                            value,
                            `processLoads[${index}].insurer`,
                          )
                        }
                        options={insurerOptions}
                        onOpen={() =>
                          getInsurerOptions(setInsurerOptions, 1, "")
                        }
                        onBottomScroll={() =>
                          onBottomScrollInsurer(
                            setInsurerOptions,
                            searchFields.insurer,
                          )
                        }
                        hasMorePagesToLoad={
                          insurerPageState.currentPage <
                          insurerPageState.lastPage
                        }
                        onInputChange={(e, value) => {
                          //condição pra não fazer a requisição na primeira atualização do input
                          if (e !== null) {
                            setSearchFields((prev) => ({
                              ...prev,
                              insurer: value,
                            }));
                            getInsurerOptions(setInsurerOptions, 1, value);
                          }
                        }}
                      />
                    )}
                  />
                </Grid>
              </Fragment>
            )}
          </Grid>
        </React.Fragment>
      ))}
    </DefaultCardStructure>
  );
};

export default GeneralLoadInfo;
